/**
 * Templates + daily notes. Convention over configuration:
 *  - a root folder titled "Templates" holds template notes
 *  - a root folder titled "Daily notes" collects day notes (auto-created)
 *  - a template titled "Daily" seeds new day notes
 * Placeholders substituted at instantiation: {{date}}, {{time}}, {{title}}.
 */
import { getData } from "@/data/store";
import { openItem } from "./nav";
import type { WorkspaceItem } from "@hydra-notes/shared";

const TEMPLATES_FOLDER = "templates";
const DAILY_FOLDER = "daily notes";

function findRootFolder(title: string): WorkspaceItem | undefined {
  return getData()
    .workspace.getSnapshot()
    .find((i) => i.type === "folder" && !i.trashedAt && i.parentId === null && i.title.trim().toLowerCase() === title);
}

export function listTemplates(): WorkspaceItem[] {
  const folder = findRootFolder(TEMPLATES_FOLDER);
  if (!folder) return [];
  return getData().workspace.childrenOf(folder.id).filter((i) => i.type === "note");
}

function substitute(text: string, title: string): string {
  const now = new Date();
  return text
    .replaceAll("{{date}}", now.toISOString().slice(0, 10))
    .replaceAll("{{time}}", now.toTimeString().slice(0, 5))
    .replaceAll("{{title}}", title);
}

/** Create a note seeded from a template. New doc, safe to seed from text. */
export async function createFromTemplate(templateId: string, parentId: string | null = null, title = ""): Promise<string> {
  const data = getData();
  const md = await data.notes.readMarkdown(templateId);
  const id = data.workspace.createItem("note", parentId, title);
  const handle = data.notes.open(id);
  await handle.whenLoaded;
  handle.doc.transact(() => {
    handle.text.insert(0, substitute(md, title));
  });
  handle.release();
  openItem(id, { newTab: true });
  return id;
}

export function todayTitle(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

/** Open today's daily note, creating folder/note (from "Daily" template) as needed. */
export async function openDailyNote(): Promise<void> {
  const data = getData();
  const title = todayTitle();

  let folder = findRootFolder(DAILY_FOLDER);
  if (!folder) {
    const folderId = data.workspace.createItem("folder", null, "Daily notes");
    folder = data.workspace.getItem(folderId);
  }
  if (!folder) return;

  const existing = data.workspace
    .childrenOf(folder.id)
    .find((i) => i.type === "note" && i.title === title);
  if (existing) {
    openItem(existing.id);
    return;
  }

  const daily = listTemplates().find((t) => t.title.trim().toLowerCase() === "daily");
  if (daily) {
    await createFromTemplate(daily.id, folder.id, title);
  } else {
    const id = data.workspace.createItem("note", folder.id, title);
    openItem(id, { newTab: true });
  }
}

/** Ensure a Templates folder exists (used by the "new template" command). */
export function ensureTemplatesFolder(): string {
  const existing = findRootFolder(TEMPLATES_FOLDER);
  if (existing) return existing.id;
  return getData().workspace.createItem("folder", null, "Templates");
}
